import { type ApolloClient } from "@apollo/client";
import {
  DeviceMenuLocation,
  type Location,
  type LocationDeviceConfig,
  OrientationType,
  type ProfileLayoutScreenModel,
  type ProfileModel,
  Resolution,
  ScreenType,
} from "@package/ui-gql/index-data-access";
import { type SliceWallState } from "../redux/slice-wall";
import { EventEmitter } from "../utils/event-emitter";
import { getDevice, getLocation, getProfile } from "../utils/wall-utils-gql";

type DeviceUpdatedEventPayload = {
  device?: SliceWallState["device"];
  deviceId?: string;
  location?: string;
  profile?: string;
  profileScreen?: ProfileLayoutScreenModel;
  screenMenuLocation?: DeviceMenuLocation;
  type?: ScreenType;
};
type DeviceEngineEvents = {
  "device-updated": (update: DeviceUpdatedEventPayload) => void;
};

export class WallEngineDevice extends EventEmitter<DeviceEngineEvents> {
  private activating: boolean = false;

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly location?: string,
    private readonly profile?: string,
    private readonly device?: string,
  ) {
    super();
  }

  public async activate(): Promise<void> {
    if (this.activating) {
      return;
    }
    this.activating = true;
    console.log("[wall-engine-device] Activating...");

    let payload: DeviceUpdatedEventPayload = {};

    let locationModel: Location | undefined = undefined;
    let profileModel: ProfileModel | undefined = undefined;

    if (this.device) {
      const device = await getDevice(this.client, this.device);
      if (device) {
        payload = {
          ...payload,
          deviceId: this.device,
          screenMenuLocation: device.config?.menu ?? DeviceMenuLocation.None,
        };

        if (device.location) {
          locationModel = await getLocation(this.client, device.location);

          if (locationModel?.profile) {
            profileModel = await getProfile(this.client, locationModel.profile);
          }
        }
      }

      payload = {
        ...payload,
        deviceId: this.device || undefined,
      };
    }

    if (this.location) {
      locationModel = await getLocation(this.client, this.location);

      if (locationModel?.profile) {
        profileModel = await getProfile(this.client, locationModel.profile);
      }
    }

    if (this.profile) {
      profileModel = await getProfile(this.client, this.profile);
    }

    if (locationModel?.id) {
      payload = {
        ...payload,
        location: locationModel.id ?? this.location,
      };
    }
    if (locationModel?.device) {
      payload = {
        ...payload,
        device: locationModel.device,
      };

      if (profileModel) {
        window?.electron?.ipcApplyDeviceConfig({
          fullscreen: !!locationModel.device?.screens?.fullscreen,
          bounds: calculateDeviceBounds(locationModel.device, profileModel),
        });
      }
    }
    if (profileModel?.id) {
      payload = {
        ...payload,
        profile: profileModel.id ?? this.profile,
      };
    }
    if (profileModel?.screen) {
      payload = {
        ...payload,
        profileScreen: profileModel.screen,
      };
    }
    if (profileModel?.type) {
      payload = {
        ...payload,
        type: profileModel.type,
      };
    }

    this.emit("device-updated", payload);

    console.log("[wall-engine-device] Activation completed", payload);
    this.activating = false;
  }
}

function calculateDeviceBounds(
  device: LocationDeviceConfig,
  profile: ProfileModel,
): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const x =
    (device.screens?.overscan?.left ? -device.screens.overscan.left : 0) +
    (device.screens?.screenOffset?.x ?? 0);
  const y =
    (device.screens?.overscan?.top ? -device.screens.overscan.top : 0) +
    (device.screens?.screenOffset?.y ?? 0);
  let width = 800;
  let height = 600;

  if (profile.type === ScreenType.Custom) {
    width = profile.screen?.size?.width ?? 800;
    height = profile.screen?.size?.height ?? 600;
  } else if (profile.type === ScreenType.Tv) {
    const multiplierX =
      profile.screen?.resolution === Resolution.Fhd ? 1080 : 2160;
    const multiplierY =
      profile.screen?.resolution === Resolution.Fhd ? 1920 : 3840;
    const multiplierXOrientation =
      profile.screen?.orientation === OrientationType.Portrait
        ? multiplierX
        : multiplierY;
    const multiplierYOrientation =
      profile.screen?.orientation === OrientationType.Portrait
        ? multiplierY
        : multiplierX;

    width =
      (profile.screen?.columns ?? 1) * multiplierXOrientation +
      (device.screens?.overscan?.left ?? 0) +
      (device.screens?.overscan?.right ?? 0);
    height =
      (profile.screen?.rows ?? 1) * multiplierYOrientation +
      (device.screens?.overscan?.top ?? 0) +
      (device.screens?.overscan?.bottom ?? 0);
  }

  return {
    x,
    y,
    width,
    height,
  };
}
